import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import toast, { Toaster } from 'react-hot-toast';

import { styles } from "../styles";
import { EarthCanvas } from "./canvas";
import { SectionWrapper } from "../hoc";
import { slideIn } from "../utils/motion";

const Contact = () => {
  const formRef = useRef();
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  
  // Custom toast notification function
  const showToast = (message, type = 'default') => {
    const options = {
      duration: 4000,
      position: 'top-center',
      style: {
        background: '#1e1e1e',
        color: '#fff',
        padding: '12px 20px',
        borderRadius: '8px',
        fontSize: '14px',
        fontFamily: 'Poppins, sans-serif',
        border: '1px solid #333',
      }
    };

    switch(type) {
      case 'success':
        toast.success(message, {
          ...options,
          style: { ...options.style, border: '1px solid #52c41a', color: '#52c41a' },
          iconTheme: { primary: '#52c41a', secondary: '#fff' }
        });
        break;
      case 'error':
        toast.error(message, {
          ...options,
          style: { ...options.style, border: '1px solid #ff4d4f', color: '#ff4d4f' },
          iconTheme: { primary: '#ff4d4f', secondary: '#fff' }
        });
        break;
      case 'loading':
        return toast.loading(message, options);
      default:
        toast(message, options);
    }
  };

  const handleChange = (e) => {
    const { target } = e;
    const { name, value } = target;

    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!form.name || !form.email || !form.message) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    setLoading(true);
    const loadingToast = showToast('Sending your message...', 'loading');

    try {
      // Debug: Log all environment variables
      console.log('Environment Variables:', import.meta.env);
      
      // Try different ways to access the variables
      const serviceId = import.meta.env.VITE_APP_EMAILJS_SERVICE_ID || 
                       process.env.VITE_APP_EMAILJS_SERVICE_ID;
      const templateId = import.meta.env.VITE_APP_EMAILJS_TEMPLATE_ID || 
                        process.env.VITE_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = import.meta.env.VITE_APP_EMAILJS_PUBLIC_KEY || 
                       process.env.VITE_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        throw new Error(`Missing required EmailJS environment variables. 
          Service ID: ${serviceId ? 'Found' : 'Missing'}
          Template ID: ${templateId ? 'Found' : 'Missing'}
          Public Key: ${publicKey ? 'Found' : 'Missing'}`);
      }

      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: form.name,
          to_name: "Hussnain Zafar",
          from_email: form.email,
          to_email: "hussnainzafar95@gmail.com",
          message: form.message,
        },
        publicKey
      );

      toast.dismiss(loadingToast);
      showToast('Message sent successfully!', 'success');
      
      // Reset form
      setForm({
        name: "",
        email: "",
        message: "",
      });
      
    } catch (error) {
      console.error('Email sending failed:', error);
      toast.dismiss(loadingToast);
      showToast('Failed to send message. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: '#1e1e1e',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'Poppins, sans-serif',
            border: '1px solid #333',
          },
          success: {
            duration: 4000,
            style: {
              border: '1px solid #52c41a',
              color: '#52c41a',
            },
          },
          error: {
            duration: 4000,
            style: {
              border: '1px solid #ff4d4f',
              color: '#ff4d4f',
            },
          },
          loading: {
            style: {
              border: '1px solid #1890ff',
              color: '#1890ff',
            },
          },
        }}
      />
      <div
        className={`xl:mt-12 flex xl:flex-row flex-col-reverse gap-10 overflow-hidden`}
      >
        <motion.div
          variants={slideIn("left", "tween", 0.2, 1)}
          className='flex-[0.75] bg-black-100 p-8 rounded-2xl'
        >
          <p className={styles.sectionSubText}>Get in touch</p>
          <h3 className={styles.sectionHeadText}>Contact.</h3>

          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className='mt-12 flex flex-col gap-8'
          >
            <label className='flex flex-col'>
              <span className='text-white font-medium mb-4'>Your Name</span>
              <input
                type='text'
                name='name'
                value={form.name}
                onChange={handleChange}
                placeholder="What's a good name?"
                className='bg-tertiary py-4 px-6 placeholder:text-secondary text-white rounded-lg outline-none border-none font-medium'
              />
            </label>
            <label className='flex flex-col'>
              <span className='text-white font-medium mb-4'>Your email</span>
              <input
                type='email'
                name='email'
                value={form.email}
                onChange={handleChange}
                placeholder="What's your email address?"
                className='bg-tertiary py-4 px-6 placeholder:text-secondary text-white rounded-lg outline-none border-none font-medium'
              />
            </label>
            <label className='flex flex-col'>
              <span className='text-white font-medium mb-4'>Your Message</span>
              <textarea
                rows={7}
                name='message'
                value={form.message}
                onChange={handleChange}
                placeholder='What would you like to say?'
                className='bg-tertiary py-4 px-6 placeholder:text-secondary text-white rounded-lg outline-none border-none font-medium'
              />
            </label>

            <button
              type='submit'
              className='bg-tertiary py-3 px-8 rounded-xl outline-none w-fit text-white font-bold shadow-md shadow-primary'
            >
              {loading ? "Sending..." : "Send"}
            </button>
          </form>
        </motion.div>

        <motion.div
          variants={slideIn("right", "tween", 0.2, 1)}
          className='xl:flex-1 xl:h-auto md:h-[550px] h-[350px]'
        >
          <EarthCanvas />
        </motion.div>
      </div>
    </>
  );
};

export default SectionWrapper(Contact, "contact");
